import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import {
  clearCreateDocumentExcelAccount,
  clearDocumentExcelAccUpload,
  clearDocumentExcelAccountError,
  clearDocumentExcelUploadStatus,
  requestCreateDocumentExcelAccount,
  requestDocumentExcelAccUpload,
  requestListBusiness,
  useAccountingDispatch,
  useAccountingState,
  useAuthState,
  useBusinessTransactionDispatch,
  useBusinessTransactionState,
} from "../../Context";
import {
  classNames,
  convertToTitleCase,
  getAccountingFileName,
  getErrorMessages,
} from "../../Utils";
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
  Transition,
} from "@headlessui/react";
import {
  CheckIcon,
  DocumentIcon,
  SelectorIcon,
} from "@heroicons/react/outline";
import { Button } from "../../Components/Button";
import { Modal } from "../../Components/Modal";
import { ModalConfirm } from "../../Components/ModalConfirm";
import ErrorStateTable from "../../Components/ErrorStateTable";
import { downloadData, getUrl } from "aws-amplify/storage";

const AccountImport = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const businessIdParam = searchParams.get("id");
  const { onboarding } = location.state || {};

  const businessDispatch = useBusinessTransactionDispatch();
  const accountingDispatch = useAccountingDispatch();

  const { user } = useAuthState();
  const { businesses, loading: businessLoading } =
    useBusinessTransactionState();
  const {
    uploadError,
    progressUpload,
    storageRequest,
    uploadLoading,
    uploadSuccess,
    excelUpload,
    excelAccountsLoading,
    excelAccountsSuccess,
    excelAccountsError,
  } = useAccountingState();

  const [showProgressModal, setShowProgressModal] = useState(false);
  const [modal, setModal] = useState({
    visible: false,
    title: "",
    description: "",
    type: "",
  });

  const [downloading, setdownloading] = useState(false);
  const [downloadProgress, setdownloadProgress] = useState(0);
  const [downloadDocError, setdownloadDocError] = useState(null);
  const [showDownloadErrorModal, setshowDownloadErrorModal] = useState(false);
  const downloadRef = useRef(null);

  const [fieldErrorFile, setfieldErrorFile] = useState({
    status: false,
    message: "",
  });
  const [selectedBusiness, setselectedBusiness] = useState({});
  const [selectedFile, setselectedFile] = useState("");

  useEffect(() => {
    if (!businesses.length && user.id) {
      requestListBusiness(businessDispatch, {
        owner: user.id,
        page: 1,
        limit: 10,
      });
    }
  }, [businessDispatch, businesses.length, user.id]);

  useEffect(() => {
    if (businessIdParam && businesses.length) {
      const business = businesses.find(
        (item) => item.value === businessIdParam
      );
      if (business) {
        setselectedBusiness({
          ...business,
          label: business.name,
          value: business.id,
        });
      }
    }
  }, [businessIdParam, businesses]);

  useEffect(() => {
    if (uploadSuccess) {
      setShowProgressModal(false);
    } else if (uploadError) {
      setShowProgressModal(false);
    }
  }, [uploadSuccess, uploadError]);

  useEffect(() => {
    if (fieldErrorFile.status) {
      setModal({
        visible: true,
        title: t("File Error"),
        description: t(fieldErrorFile.message),
        type: "WARNING",
      });
    }
  }, [fieldErrorFile, t]);

  useEffect(() => {
    if (excelAccountsSuccess) {
      setModal({
        visible: true,
        title: t("Success"),
        description: t("Your transaction has been created."),
        type: "SUCCESS",
      });
    } else if (excelAccountsError) {
      setModal({
        visible: true,
        title: t("Error"),
        description: t(getErrorMessages(excelAccountsError)),
        type: "ALERT",
      });
    }
  }, [excelAccountsSuccess, excelAccountsError, t]);

  useEffect(() => {
    return () => {
      clearDocumentExcelUploadStatus(accountingDispatch);
    };
  }, [accountingDispatch]);

  const handleFileUpload = (event) => {
    event.preventDefault();
    const file = event.dataTransfer
      ? event.dataTransfer.files[0]
      : event.target.files[0];
    const maxSize = 100 * 1024; // 100 KB

    if (
      file &&
      file.type ===
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" &&
      file.size <= maxSize &&
      file.name.length < 120
    ) {
      setfieldErrorFile({ status: false, message: "" });
      setselectedFile(file);
    } else {
      if (!file) {
        setfieldErrorFile({ status: true, message: "Please select a file." });
      } else if (
        file.type !==
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      ) {
        setfieldErrorFile({
          status: true,
          message: "The file format must be Excel.",
        });
        event.target.value = "";
      } else if (file.size >= maxSize) {
        setfieldErrorFile({
          status: true,
          message: "File size should not exceed 100 KB",
        });
        event.target.value = "";
      } else if (file.name.length >= 120) {
        setfieldErrorFile({
          status: true,
          message: "The file name is too long, maximum 120 characters.",
        });
        event.target.value = "";
      }
    }
  };

  const handleImportClick = (selectedFile) => {
    if (!selectedFile) {
      setfieldErrorFile({
        status: true,
        message: "Select a file before clicking Upload.",
      });
    } else {
      if (selectedBusiness.id) {
        setShowProgressModal(true);
        requestDocumentExcelAccUpload(accountingDispatch, {
          businessID: selectedBusiness.id,
          file: selectedFile,
        });
      } else {
        setfieldErrorFile({
          status: true,
          message: "Business name is required.",
        });
      }
    }
  };

  const onCreate = () => {
    requestCreateDocumentExcelAccount(accountingDispatch, {
      businessID: selectedBusiness.id,
      accounts: excelUpload.items.map((item) => ({
        code: item.code,
        name: item.name,
        category: item.category,
      })),
    });
  };

  const handleModalConfirm = () => {
    if (excelAccountsSuccess) {
      if (onboarding) {
        navigate(`/opening-balance?id=${selectedBusiness.id}`, {
          state: { onboarding: true },
        });
      } else {
        navigate(`/account?id=${selectedBusiness.id}`);
      }
      clearCreateDocumentExcelAccount(accountingDispatch);
      clearDocumentExcelAccUpload(accountingDispatch);
      clearDocumentExcelAccountError(accountingDispatch);
    } else {
      setModal((prevModal) => ({
        ...prevModal,
        visible: false,
      }));
      setfieldErrorFile({ status: false, message: "" });
    }
  };

  const handleDownloadTemplate = () => {
    downloadFile("template/accounting_account_template.xlsx");
  };

  const renderProgressBar = (label, percentage) => (
    <div className="flex-1 overflow-hidden">
      <div className="w-full">
        <p className="text-xs text-slate-500">{t(label)}</p>
        <div className="w-full mt-1 bg-gray-200 rounded-md sm:rounded-lg dark:bg-gray-700">
          <div
            className="bg-blue-600 text-xs font-medium text-blue-100 text-center p-0.5 leading-none rounded-full"
            style={{ width: `${percentage}%` }}
          >
            {percentage}%
          </div>
        </div>
      </div>
    </div>
  );

  const renderProgressModal = () => (
    <Modal
      title="Excel Document Upload Process"
      show={showProgressModal}
      onClose={() => {
        setShowProgressModal(false);
      }}
      footer={
        <div className="p-4 flex flex-row-reverse gap-2 justify-between items-center border-t border-gray-300 bg-gray-50 rounded-b-lg">
          {uploadLoading
            ? storageRequest !== null
              ? renderProgressBar(
                  "Uploading",
                  Math.round(
                    (progressUpload?.transferredBytes /
                      progressUpload?.totalBytes) *
                      100
                  ),
                  100
                )
              : renderProgressBar("Uploading Excel Document File", 100)
            : null}
        </div>
      }
    >
      <div className="bg-white p-4">
        <div className="flex flex-col justify-center items-center">
          <DocumentIcon className="h-24 w-24 text-slate-500" />
          <div className="w-full text-center">
            <p className="text-xs text-slate-500 truncate">
              {selectedFile?.name}
            </p>
          </div>
        </div>
      </div>
    </Modal>
  );

  const onDownloadProgress = (progress) => {
    const { transferredBytes, totalBytes } = progress;
    if (!totalBytes) {
      setdownloadProgress(0);
    } else {
      setdownloadProgress(transferredBytes / totalBytes);
    }
  };

  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || "download";
    const clickHandler = () => {
      setTimeout(() => {
        URL.revokeObjectURL(url);
        a.removeEventListener("click", clickHandler);
      }, 150);
    };
    a.addEventListener("click", clickHandler, false);
    a.click();
    return a;
  };

  const downloadFile = async (key) => {
    try {
      setdownloading(true);

      const request = downloadData({
        path: `public/${key}`,
        options: {
          onProgress: onDownloadProgress,
        },
      });

      downloadRef.current = request;
      const result = await request.result;
      const fileName = getAccountingFileName(key);

      downloadBlob(result.body, fileName);
    } catch (downloadError) {
      if (downloadError.message !== "canceled") {
        await handleDownloadError(key, downloadError);
      }
    } finally {
      setdownloading(false);
      setdownloadProgress(0);
    }
  };

  const handleDownloadError = async (key) => {
    try {
      const urlResult = await getUrl({
        path: `public/${key}`,
        options: {
          useAccelerateEndpoint: true,
        },
      });
      const signedURL = urlResult.url.href;
      window.open(signedURL);
    } catch (signedURLError) {
      setdownloadDocError(signedURLError);
    }
  };

  const renderFileModal = () => (
    <Modal
      title="Download File"
      show={downloading}
      onClose={() => {
        if (downloadRef.current) {
          const downloadTask = downloadRef.current;
          downloadTask.cancel();
        }
        setdownloading(false);
      }}
      footer={
        <div className="p-4 flex flex-row-reverse gap-2 justify-between items-center border-t border-gray-300 bg-gray-50 rounded-b-lg">
          <div className="flex-1 overflow-hidden">
            <div className="w-full">
              <p className="text-xs text-slate-500">{t("Downloading")}</p>
              <div className="w-full mt-1 bg-gray-200 rounded-md sm:rounded-lg dark:bg-gray-700">
                <div
                  className="bg-blue-600 text-xs font-medium text-blue-100 text-center p-0.5 leading-none rounded-full"
                  style={{
                    width: `${Math.round(downloadProgress * 100)}%`,
                  }}
                >
                  {Math.round(downloadProgress * 100)}%
                </div>
              </div>
            </div>
          </div>
        </div>
      }
    >
      <div className="bg-white p-4">
        <div className="flex justify-center">
          <DocumentIcon className="h-24 w-24 text-slate-500" />
        </div>
      </div>
    </Modal>
  );

  const renderDownloadError = () => {
    if (downloadDocError) {
      return (
        <ModalConfirm
          open={showDownloadErrorModal}
          type="ALERT"
          setOpen={(open) => {
            setshowDownloadErrorModal(open);
          }}
          title="Error"
          description={getErrorMessages(downloadDocError)}
          onConfirm={() => {
            setdownloadDocError(null);
          }}
        />
      );
    }
    return null;
  };

  function handleDragOver(event) {
    event.preventDefault();
  }

  function handleDragLeave(event) {
    event.preventDefault();
  }

  const combinedAccounts = (excelUpload.items || []).concat(
    excelUpload.invalid_items || []
  );

  const parsedErrorResponse = uploadError?.response?.body
    ? JSON.parse(uploadError.response.body)
    : null;
  const errorAccounts = parsedErrorResponse?.data?.invalid_items;

  return (
    <div className="flex flex-col gap-6">
      {renderProgressModal()}
      {renderDownloadError()}
      {renderFileModal()}
      <ModalConfirm
        open={modal.visible}
        setOpen={(open) => {
          setModal((prevModal) => ({
            ...prevModal,
            visible: open,
          }));
        }}
        title={modal.title}
        description={modal.description}
        type={modal.type}
        onConfirm={() => handleModalConfirm()}
      />

      <div className="flex sm:flex-row flex-col justify-between gap-4">
        <div className="flex sm:flex-row flex-col gap-4">
          <div>
            <Listbox
              disabled={combinedAccounts?.length > 0}
              value={selectedBusiness}
              onChange={(value) => {
                setselectedBusiness(value);
                clearDocumentExcelAccountError(accountingDispatch);
                clearDocumentExcelAccUpload(accountingDispatch);
                clearDocumentExcelUploadStatus(accountingDispatch);
              }}
            >
              {({ open }) => (
                <>
                  <ListboxButton
                    className={classNames(
                      businessLoading ||
                        businesses.length === 0 ||
                        combinedAccounts?.length > 0
                        ? "bg-gray-200 border-transparent text-gray-400 cursor-not-allowed"
                        : "bg-white",
                      "flex items-center justify-between w-full sm:w-48 py-2 px-3 border border-gray-300 rounded-md shadow-sm sm:text-sm"
                    )}
                  >
                    <span className="max-w-full ml-3 mr-2 text-sm text-ellipsis overflow-hidden sm:whitespace-nowrap">
                      {businesses.length === 0
                        ? t("No Business Profile Yet")
                        : selectedBusiness?.label || t("Select Business Name")}
                    </span>
                    {businessLoading ? (
                      <svg
                        role="status"
                        className="w-5 h-5 animate-spin text-white"
                        viewBox="0 0 100 101"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        aria-hidden="true"
                      >
                        <path
                          d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                          fill="#E5E7EB"
                        />
                        <path
                          d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                          fill="currentColor"
                        />
                      </svg>
                    ) : (
                      <SelectorIcon className="w-5 h-5 text-gray-500" />
                    )}
                  </ListboxButton>
                  <Transition
                    show={open}
                    leave="transition ease-in duration-100"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                  >
                    <ListboxOptions className="absolute z-10 mt-1 bg-white shadow-lg max-h-56 w-11/12 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm sm:w-auto">
                      {businesses.map((business) => (
                        <ListboxOption
                          key={business.value}
                          className={({ focus }) =>
                            classNames(
                              focus
                                ? "text-white bg-blue-600"
                                : "text-gray-900",
                              "cursor-default select-none relative py-2 pl-3 pr-9"
                            )
                          }
                          value={business}
                        >
                          {({ selected, focus }) => (
                            <>
                              <div className="flex items-center justify-between">
                                <span
                                  className={classNames(
                                    selected ? "font-semibold" : "font-normal",
                                    "ml-3 mr-2 block truncate"
                                  )}
                                >
                                  {business.label}
                                </span>
                                {selected ? (
                                  <span
                                    className={classNames(
                                      focus ? "text-white" : "text-blue-600",
                                      "absolute inset-y-0 right-0 flex items-center pr-4"
                                    )}
                                  >
                                    <CheckIcon className="h-5 w-5" />
                                  </span>
                                ) : null}
                              </div>
                            </>
                          )}
                        </ListboxOption>
                      ))}
                    </ListboxOptions>
                  </Transition>
                </>
              )}
            </Listbox>
          </div>
          {businesses.length === 0 && (
            <div>
              <Button
                className="w-full md:w-auto"
                onClick={() => navigate("/business/create")}
                title="Create Business Profile"
              />
            </div>
          )}
          {excelUpload?.length === 0 && (
            <div>
              <Button
                outline
                className="w-full md:w-auto"
                onClick={handleDownloadTemplate}
                title="Download Template"
              />
            </div>
          )}
        </div>
        <div className="flex sm:flex-row flex-col gap-4">
          {excelUpload?.length === 0 && (
            <div className="flex sm:flex-row flex-col gap-4">
              <div className="flex">
                <label
                  htmlFor="upload-input"
                  className="items-center whitespace-nowrap px-4 py-2 bg-gray-100 border-y border-l border-gray-300 rounded-l-md cursor-pointer text-sm text-gray-600 hover:bg-gray-200 focus:bg-blue-50 focus:outline-none disabled:opacity-25 transition "
                  onDragOver={handleDragOver}
                  onDrop={handleFileUpload}
                  onDragLeave={handleDragLeave}
                >
                  {t("Choose File")}
                  <input
                    type="file"
                    id="upload-input"
                    className="sr-only"
                    onChange={handleFileUpload}
                    accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  />
                </label>
                <div
                  className="flex w-full bg-white border border-gray-300 rounded-r-md px-4 py-2 overflow-hidden"
                  onDragOver={handleDragOver}
                  onDrop={handleFileUpload}
                  onDragLeave={handleDragLeave}
                >
                  <span className="align-middle w-36 text-gray-400 text-sm truncate">
                    {selectedFile?.name || t("File not selected")}
                  </span>
                </div>
              </div>
              <div>
                <Button
                  outline
                  className="w-full md:w-auto"
                  onClick={() => handleImportClick(selectedFile)}
                  title={t("Upload")}
                />
              </div>
            </div>
          )}

          {excelUpload?.invalid_items?.length > 0 && (
            <div>
              <Button
                outline
                disabled={excelAccountsLoading}
                className="w-full md:w-auto"
                type="button"
                title={t("Change File")}
                onClick={() => {
                  clearDocumentExcelAccountError(accountingDispatch);
                  clearDocumentExcelAccUpload(accountingDispatch);
                  setselectedFile("");
                }}
              />
            </div>
          )}

          <div>
            <Button
              className="w-full md:w-auto"
              type="button"
              disabled={excelUpload.length === 0 || excelAccountsLoading}
              loading={excelAccountsLoading}
              title={t("Create Accounting Account")}
              onClick={() => onCreate()}
            />
          </div>
        </div>
      </div>
      <div className="flex flex-col">
        <div className="overflow-x-auto -mx-4 sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full align-middle md:px-6 lg:px-8">
            <div className="overflow-hidden border border-gray-200 rounded-lg shadow-sm">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                      {t("Code")}
                    </th>
                    <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                      {t("Name")}
                    </th>
                    {excelUpload && !uploadError && (
                      <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                        {t("Category")}
                      </th>
                    )}
                    {excelUpload && !uploadError && (
                      <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                        {t("Account Type")}
                      </th>
                    )}
                    {(excelUpload?.invalid_items?.length > 0 ||
                      errorAccounts?.length > 0) && (
                      <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                        {t("Error")}
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {combinedAccounts.length > 0 ? (
                    combinedAccounts.map((item, index) => (
                      <tr key={index}>
                        <td className="px-4 py-2 font-medium text-sm text-gray-700 whitespace-nowrap ">
                          {item.code}
                        </td>
                        <td className="px-4 py-2 font-medium text-sm text-gray-700 whitespace-nowrap ">
                          {item.name}
                        </td>
                        <td className="px-4 py-2 text-sm text-left text-gray-700 whitespace-nowrap">
                          {t(convertToTitleCase(item.category))}
                        </td>
                        <td className="px-4 py-2 text-sm text-left text-gray-700 whitespace-nowrap">
                          {t(convertToTitleCase(item.type))}
                        </td>
                        {(excelUpload?.invalid_items?.length > 0 ||
                          errorAccounts?.length > 0) && (
                          <td className="px-4 py-2 text-sm text-left text-red-600 whitespace-nowrap">
                            {t(item.error)}
                          </td>
                        )}
                      </tr>
                    ))
                  ) : errorAccounts?.length > 0 ? (
                    errorAccounts.map((item) => (
                      <tr key={item.id}>
                        <td className="px-4 py-2 font-medium text-sm text-gray-700 whitespace-nowrap ">
                          {item.code}
                        </td>
                        <td className="px-4 py-2 font-medium text-sm text-gray-700 whitespace-nowrap ">
                          {item.name}
                        </td>
                        <td className="px-4 py-2 text-sm text-left text-red-600 whitespace-nowrap">
                          {t(item.error)}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <ErrorStateTable
                      colSpan={4}
                      error={uploadError}
                      emptyTitle="No import data"
                    />
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountImport;
